import type { ComponentProps } from 'react';
import type { SingleComboBox } from '@itandi/itandi-bb-ui/SingleComboBox';
import type { Th } from '@itandi/itandi-bb-ui/Th';
import type { DealType } from './Deal';
import type { Subtype } from './Subtype';
import type { Staff } from './Staff';

export type CompanyDealTodo = {
  id: number;
  completed: boolean;
  title: string;
  memo: string | null;
  dueDate: string | null;
  completedAt: string | null;
  createdAt: string;
  storeName: string | null;
  deal: {
    id: number;
    name: string;
    roomNumber: string | null;
    dealType: DealType;
    dealSubtype: Subtype;
    staff: { id: number; name: string };
  };
  originVendorCompany: {
    id: number;
    name: string;
    vendorUser: { id: number; name: string } | null;
  } | null;
  staff: { id: number; name: string } | null;
};

export type TodoSortKey = 'id' | 'dueDate';

export const unassignedStaff = { label: '担当者未設定', value: '-1' } as const;

export type CompanyDealTodoSearchCondition = {
  page?: number;
  authId?: number | null;
  completed?: 'true' | 'false' | 'all';
  text?: string;
  staffId?: number | null;
  dueDateFrom?: string;
  dueDateTo?: string;
  dealName?: string;
  roomNumber?: string;
  dealTypes?: DealType[];
  companyVendorId?: number | null;
  sortKey?: TodoSortKey;
  sortOrder?: ComponentProps<typeof Th>['sort'];
};

export const SEARCH_CONDITION_KEYS = [
  'page',
  'authId',
  'completed',
  'text',
  'staffId',
  'dueDateFrom',
  'dueDateTo',
  'dealName',
  'roomNumber',
  'dealTypes',
  'companyVendorId',
  'sortKey',
  'sortOrder'
] as const satisfies ReadonlyArray<keyof CompanyDealTodoSearchCondition>;

const COMPLETED_VALUES = [
  'true',
  'false',
  'all'
] as const satisfies ReadonlyArray<
  Exclude<CompanyDealTodoSearchCondition['completed'], undefined>
>;

export const isQueryCompleted = (
  query: string
): query is (typeof COMPLETED_VALUES)[number] =>
  COMPLETED_VALUES.some((completedValue) => completedValue === query);

// /** 担当者の並び順を未設定→ログイン担当者→その他に並び替えたSingleComboBoxのOptions */
export const staffOptions = (
  staffs: Staff[],
  loginStaff: Staff | null
): ComponentProps<typeof SingleComboBox>['options'] => {
  const loginStaffId = loginStaff?.id;

  const orderedStaffs = staffs.toSorted((a, b) => {
    if (loginStaffId == null) return 0;
    if (a.id === loginStaffId) return -1;
    if (b.id === loginStaffId) return 1;
    return 0;
  });

  return [
    unassignedStaff,
    ...orderedStaffs.map((s) => ({ label: s.name, value: String(s.id) }))
  ];
};

export const selectedStaffOption = (
  searchCondition: CompanyDealTodoSearchCondition,
  selectedStaff: Staff | null
): ComponentProps<typeof SingleComboBox>['value'] => {
  if (searchCondition.staffId === Number(unassignedStaff.value)) {
    return unassignedStaff;
  }
  return (
    selectedStaff && {
      label: selectedStaff.name,
      value: String(selectedStaff.id)
    }
  );
};
